import axios from 'axios'

/*
  Calculator endpoints ported from CGS2022.

  ── ON THE HOST, READ THIS BEFORE ADDING A CALL ──
  CGS reaches the API through `ajaxManager` in wwwroot/js/site.js, which sniffs the browser's
  own hostname and prepends one of three origins:

      local*          -> https://localhost:44346
      dev.goldstar*   -> https://devapi.goldstarfinancial.com
      cgs.goldstar*   -> https://api.goldstarfinancial.com

  SwiftProcessingSPA's VUE_APP_API_ENDPOINT is https://api.goldstarfinancial.com/ — the same
  origin CGS resolves to in production — so the paths below are the CGS paths verbatim and
  they land on the same controllers.

  The trap to avoid: this app ALSO has VUE_APP_SWIFT_API_ENDPOINT
  (https://swiftapi.goldstarfinancial.com/). Both are commonly referred to as "the API".
  A CGS /api/Calculator/... path put on the Swift host silently 404s or, worse, hits a
  different controller. Everything in this file is deliberately on API_ENDPOINT.

  Auth: CGS reads a `token` cookie; here the caller passes the MSAL access token from
  auth.acquireTokenResponse(). Same Bearer scheme on the wire.

  Envelope: every one of these returns GSWebAPIv2's ServiceResponse — { isSuccess, message,
  data } — and GSWebAPIv2 lower-cases every JSON property name app-wide (LowerCaseNamingPolicy
  in Startup.cs). Read fields through `pick` below rather than by exact case, or a rename on
  the server side turns into a silent undefined here.
*/

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT

function authHeaders (token) {
  return token ? { Authorization: 'Bearer ' + token } : {}
}

// Matches a key regardless of casing or separators, because the API's naming policy and the
// raw Encompass payloads disagree about camelCase / PascalCase / all-lowercase.
export function pick (obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined
  const norm = k => String(k).toLowerCase().replace(/[_-]/g, '')
  const wanted = keys.map(norm)
  for (const actual of Object.keys(obj)) {
    if (wanted.includes(norm(actual)) && obj[actual] !== undefined && obj[actual] !== null) {
      return obj[actual]
    }
  }
  return undefined
}

// Unwraps ServiceResponse, throwing the server's own message so the caller can show it.
export function unwrap (response) {
  const body = response?.data || {}
  const ok = pick(body, 'isSuccess')
  const data = pick(body, 'data')
  if (ok === false) throw new Error(pick(body, 'message') || 'The request was not successful.')
  return data
}

export default {
  // CGS: fetchLoanPayoffLiabilities() in CalculatorV2.vue
  // data -> { loanNumber, loanGuid, liabilities: [{ accountType, companyName, unpaidBalance,
  //                                                 monthlyAmount, defaultPayoff }] }
  GetLoanPayoffLiabilities (loanNumber, loanGuid, token) {
    const url = API_ENDPOINT +
      `api/Calculator/GetLoanPayoffLiabilities/?loanNumber=${encodeURIComponent(loanNumber || '')}` +
      `&loanGuid=${encodeURIComponent(loanGuid || '')}`
    return axios.get(url, { headers: { ...authHeaders(token), 'Content-Type': 'application/json' } })
  },

  // CGS: LoanLookupFlyout.loadRecentLoans()
  // data -> raw Encompass pipeline rows: { loanId, fields: { "Loan.LoanNumber", "Fields.4000", ... } }
  GetRecentLoans (azureId, token) {
    const url = API_ENDPOINT + `api/Loans/GetRecentLoans?azureId=${encodeURIComponent(azureId || '')}`
    return axios.get(url, { headers: authHeaders(token) })
  },

  // CGS: LoanLookupFlyout.runSearch()
  // data -> flat LoanPipelineSearchResultDto[]
  SearchLoanPipeline (azureId, searchTerm, token, take = 25) {
    const url = API_ENDPOINT +
      `api/Loans/SearchLoanPipeline?azureId=${encodeURIComponent(azureId || '')}` +
      `&searchTerm=${encodeURIComponent(searchTerm || '')}&take=${take}`
    return axios.get(url, { headers: authHeaders(token) })
  },

  // The latest 30-day Average SOFR (NY Fed), the ARM index.
  // data -> { rate (percent), effectiveDate ("yyyy-MM-dd") }; on failure isSuccess is false and
  // there is no data.
  GetSofrIndex (token) {
    const url = API_ENDPOINT + 'api/Calculator/GetSofrIndex'
    return axios.get(url, { headers: authHeaders(token) })
  },

  // CGS: generateCalculatorPDF() in CalculatorV2.vue
  // data -> { pdfData (base64), pdfUrl }
  GenerateCalculatorPDFV2 (azureId, payload, token) {
    const url = API_ENDPOINT + `api/Calculator/GenerateCalculatorPDFV2/?azureId=${encodeURIComponent(azureId || '')}`
    return axios.post(url, payload, {
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' }
    })
  }
}
